/*
* === StaffDashboards.js (Final - With Interview Scheduler) ===
*/
import React, { useEffect, useState } from 'react';
import { 
    Container, Typography, Grid, Card, CardContent, Button, Box, Alert, 
    CircularProgress, Dialog, DialogTitle, DialogContent, DialogActions, 
    TextField, List, ListItem, ListItemText, IconButton, Chip 
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import StatCard from '../../components/StatCard';
import AssignmentTurnedInIcon from '@mui/icons-material/AssignmentTurnedIn';
import RateReviewIcon from '@mui/icons-material/RateReview';
import PaymentsIcon from '@mui/icons-material/Payments';
import SearchIcon from '@mui/icons-material/Search';
import EventIcon from '@mui/icons-material/Event'; // New Icon
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import api from '../../services/api';
import { useAuth } from '../../context/AuthContext';

// --- SHARED DATA HOOK ---
const useDashboardData = () => {
    const { token } = useAuth();
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        if (!token) return;
        api.get('/admin/dashboard-data', { headers: { Authorization: `Bearer ${token}` } })
           .then(res => setData(res.data))
           .catch(err => console.error(err))
           .finally(() => setLoading(false));
    }, [token]);

    return { data, loading };
};

// --- INTERVIEW SCHEDULER MODAL ---
const InterviewSchedulerModal = ({ open, onClose, token }) => {
    const [slots, setSlots] = useState([]);
    const [newSlot, setNewSlot] = useState({ date: '', startTime: '', endTime: '', link: '' });
    const [loading, setLoading] = useState(false);

    const fetchSlots = async () => {
        try {
            const res = await api.get('/admin/interviews/my-slots', { headers: { Authorization: `Bearer ${token}` } });
            setSlots(res.data);
        } catch (e) { console.error("Error fetching slots"); }
    };

    useEffect(() => { if (open) fetchSlots(); }, [open]);

    const handleAddSlot = async () => {
        if (!newSlot.date || !newSlot.startTime || !newSlot.endTime) return alert("Please fill all time fields.");
        setLoading(true);
        try {
            const startDateTime = `${newSlot.date}T${newSlot.startTime}:00`;
            const endDateTime = `${newSlot.date}T${newSlot.endTime}:00`;
            
            await api.post('/admin/interviews/slots', {
                startTime: startDateTime,
                endTime: endDateTime,
                meetingLink: newSlot.link
            }, { headers: { Authorization: `Bearer ${token}` } });
            
            setNewSlot({ ...newSlot, startTime: '', endTime: '' }); // Reset times but keep date/link for convenience
            fetchSlots();
        } catch (e) { alert("Failed to add slot."); }
        finally { setLoading(false); }
    };

    const handleDelete = async (id) => {
        try {
            await api.delete(`/admin/interviews/slots/${id}`, { headers: { Authorization: `Bearer ${token}` } });
            fetchSlots();
        } catch (e) { alert("Failed to delete slot (it might be booked)."); }
    };

    return (
        <Dialog open={open} onClose={onClose} fullWidth maxWidth="md">
            <DialogTitle>Manage Interview Availability</DialogTitle>
            <DialogContent>
                <Grid container spacing={2} sx={{ mt: 1, mb: 3 }}>
                    <Grid item xs={12} sm={3}>
                        <TextField type="date" label="Date" InputLabelProps={{ shrink: true }} fullWidth size="small" value={newSlot.date} onChange={e => setNewSlot({...newSlot, date: e.target.value})} />
                    </Grid>
                    <Grid item xs={6} sm={2}>
                        <TextField type="time" label="Start" InputLabelProps={{ shrink: true }} fullWidth size="small" value={newSlot.startTime} onChange={e => setNewSlot({...newSlot, startTime: e.target.value})} />
                    </Grid>
                    <Grid item xs={6} sm={2}>
                        <TextField type="time" label="End" InputLabelProps={{ shrink: true }} fullWidth size="small" value={newSlot.endTime} onChange={e => setNewSlot({...newSlot, endTime: e.target.value})} />
                    </Grid>
                    <Grid item xs={12} sm={3}>
                        <TextField label="Meeting Link (Optional)" fullWidth size="small" value={newSlot.link} onChange={e => setNewSlot({...newSlot, link: e.target.value})} placeholder="Zoom/Teams URL" />
                    </Grid>
                    <Grid item xs={12} sm={2}>
                        <Button variant="contained" fullWidth onClick={handleAddSlot} disabled={loading} startIcon={<AddIcon />}>Add</Button>
                    </Grid>
                </Grid>
                
                <Typography variant="subtitle2" gutterBottom>Your Slots</Typography>
                <List dense sx={{ bgcolor: 'background.paper', border: '1px solid #eee', borderRadius: 1, maxHeight: 300, overflow: 'auto' }}>
                    {slots.length === 0 && <ListItem><ListItemText primary="No slots added." /></ListItem>}
                    {slots.map(slot => (
                        <ListItem key={slot.Id} divider secondaryAction={
                            <IconButton edge="end" onClick={() => handleDelete(slot.Id)} disabled={slot.Status === 'Booked'}>
                                <DeleteIcon color={slot.Status === 'Booked' ? 'disabled' : 'error'} />
                            </IconButton>
                        }>
                            <ListItemText 
                                primary={`${new Date(slot.StartTime).toLocaleDateString()} | ${new Date(slot.StartTime).toLocaleTimeString()} - ${new Date(slot.EndTime).toLocaleTimeString()}`}
                                secondary={slot.Status === 'Booked' ? `Booked by: ${slot.ApplicantName}` : 'Available'}
                            />
                            {slot.Status === 'Booked' && <Chip label="BOOKED" color="success" size="small" sx={{ mr: 2 }} />}
                        </ListItem>
                    ))}
                </List>
            </DialogContent>
            <DialogActions><Button onClick={onClose}>Close</Button></DialogActions>
        </Dialog>
    );
};

// --- 1. REVIEWER DASHBOARD ---
export const ReviewerDashboard = () => {
    const navigate = useNavigate();
    const { token } = useAuth();
    const { data, loading } = useDashboardData();
    const [isSchedulerOpen, setIsSchedulerOpen] = useState(false);

    if (loading) return <Box sx={{display:'flex', justifyContent:'center', mt:4}}><CircularProgress/></Box>;

    return (
        <Container maxWidth="xl" sx={{ mt: 4 }}>
            <Typography variant="h4" fontWeight="bold" gutterBottom>Reviewer Dashboard</Typography>
            <Grid container spacing={3} sx={{ mb: 4 }}>
                <Grid item xs={12} sm={6} md={4}>
                    <StatCard title="Assigned to Me" value={data?.stats?.myAssignedQueue || 0} icon={<RateReviewIcon />} color="linear-gradient(45deg, #2196F3, #21CBF3)" />
                </Grid>
                <Grid item xs={12} sm={6} md={4}>
                    <StatCard title="Under Review" value={data?.stats?.underReview || 0} icon={<AssignmentTurnedInIcon />} color="linear-gradient(45deg, #FF9800, #FFC107)" />
                </Grid>
            </Grid>

            <Grid container spacing={3}>
                <Grid item xs={12} md={6}>
                    <Card sx={{ height: '100%' }}>
                        <CardContent>
                            <Typography variant="h6" gutterBottom>Quick Actions</Typography>
                            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                                <Button variant="contained" startIcon={<SearchIcon />} onClick={() => navigate('/admin/search?filter=assigned_to_me')}>
                                    Review Assigned Apps
                                </Button>
                                <Button variant="outlined" onClick={() => navigate('/admin/search')}>
                                    Search All Applications
                                </Button>
                            </Box>
                        </CardContent>
                    </Card>
                </Grid>
                <Grid item xs={12} md={6}>
                    <Card sx={{ height: '100%' }}>
                        <CardContent>
                            <Typography variant="h6" gutterBottom>Interview Management</Typography>
                            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                                Set up your availability for applicant interviews.
                            </Typography>
                            <Button variant="contained" color="secondary" startIcon={<EventIcon />} onClick={() => setIsSchedulerOpen(true)}>
                                Manage Availability
                            </Button>
                        </CardContent>
                    </Card>
                </Grid>
            </Grid>

            <InterviewSchedulerModal open={isSchedulerOpen} onClose={() => setIsSchedulerOpen(false)} token={token} />
        </Container>
    );
};

// --- 2. APPROVER DASHBOARD (Unchanged) ---
export const ApproverDashboard = () => {
    const navigate = useNavigate();
    const { data, loading } = useDashboardData();

    if (loading) return <Box sx={{display:'flex', justifyContent:'center', mt:4}}><CircularProgress/></Box>;

    return (
        <Container maxWidth="xl" sx={{ mt: 4 }}>
            <Typography variant="h4" fontWeight="bold" gutterBottom>Approver Dashboard</Typography>
            <Grid container spacing={3} sx={{ mb: 4 }}>
                <Grid item xs={12} sm={6} md={4}>
                    <StatCard title="Pending Approval" value={data?.stats?.underReview || 0} icon={<AssignmentTurnedInIcon />} color="linear-gradient(45deg, #4CAF50, #8BC34A)" />
                </Grid>
            </Grid>
            <Card>
                <CardContent>
                    <Typography variant="h6" gutterBottom>Approval Queue</Typography>
                    <Button variant="contained" color="success" onClick={() => navigate('/admin/search?status=Under Review')}>View Queue</Button>
                </CardContent>
            </Card>
        </Container>
    );
};

// --- 3. FINANCE DASHBOARD (Unchanged) ---
export const FinanceDashboard = () => {
    const navigate = useNavigate();
    const { data, loading } = useDashboardData();

    if (loading) return <Box sx={{display:'flex', justifyContent:'center', mt:4}}><CircularProgress/></Box>;

    return (
        <Container maxWidth="xl" sx={{ mt: 4 }}>
            <Typography variant="h4" fontWeight="bold" gutterBottom>Finance Dashboard</Typography>
            <Grid container spacing={3} sx={{ mb: 4 }}>
                <Grid item xs={12} sm={6} md={4}>
                    <StatCard title="Approved & Unfunded" value="--" icon={<PaymentsIcon />} color="linear-gradient(45deg, #F44336, #E91E63)" />
                </Grid>
                <Grid item xs={12} sm={6} md={4}>
                    <StatCard title="Total Applications" value={data?.stats?.totalApplications || 0} icon={<AssignmentTurnedInIcon />} color="linear-gradient(45deg, #9C27B0, #673AB7)" />
                </Grid>
            </Grid>
            <Card>
                <CardContent>
                    <Typography variant="h6" gutterBottom>Financial Actions</Typography>
                    <Box sx={{ display: 'flex', gap: 2 }}>
                        <Button variant="contained" startIcon={<PaymentsIcon />} onClick={() => navigate('/admin/search?status=Approved')}>Process Payments</Button>
                        <Button variant="outlined" onClick={() => navigate('/admin/reports')}>Financial Reports</Button>
                    </Box>
                </CardContent>
            </Card>
        </Container>
    );
};